import { GraduationCap, CheckCircle2, Award, Globe2, Stethoscope } from "lucide-react";
import { EDUCATION, PROFILE } from "../data/content";

export const SobreMim = () => {
  return (
    <section
      id="sobre"
      data-testid="about-section"
      className="relative py-20 md:py-28 bg-background"
    >
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold-500 to-transparent opacity-60" />
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-5">
          <div className="p-6 rounded-2xl bg-gradient-to-br from-royal-800 to-royal-900 text-white shadow-lg">
            <Award size={28} className="text-gold-500" />
            <div className="mt-4 font-serif-display text-5xl">26+</div>
            <div className="mt-2 text-sm text-white/80">
              anos de prática clínica em consultório
            </div>
            <div className="mt-5 pt-5 border-t border-white/15 text-xs tracking-widest uppercase text-gold-500 font-semibold">
              {PROFILE.crp}
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-br from-gold-50 to-gold-100/60 border-2 border-gold-500/30">
            <Stethoscope size={22} className="text-gold-700" />
            <p className="mt-3 text-xs tracking-widest uppercase text-gold-700 font-semibold">
              Pacientes atendidos
            </p>
            <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
              {PROFILE.patients.join(" • ")}
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-border">
            <Globe2 size={22} className="text-royal-800" />
            <p className="mt-3 text-xs tracking-widest uppercase text-royal-800 font-semibold">
              Idiomas de atendimento
            </p>
            <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
              {PROFILE.languages.join(" • ")}
            </p>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-royal-800/10 border border-royal-800/30">
            <span className="text-[11px] tracking-[0.2em] uppercase text-royal-800 font-semibold">
              Sobre a profissional
            </span>
          </div>
          <h2 className="mt-4 font-serif-display text-3xl md:text-5xl text-royal-900 leading-tight">
            Uma vida dedicada <br /> ao cuidado humano.
          </h2>

          <div className="mt-8 space-y-5 text-base md:text-lg text-foreground/85 leading-relaxed">
            <p>
              Desde muito cedo, a Dra. Nivalda sempre teve grande interesse por leitura, estudo e
              cuidado com as pessoas. Seu caminho começou na área da saúde com curso de primeiros
              socorros, atuação como atendente hospitalar e, posteriormente, formação como
              instrumentadora cirúrgica.
            </p>
            <p>
              Acompanhar de perto a vulnerabilidade humana em momentos delicados despertou o
              desejo de compreender o ser humano em profundidade. Graduou-se em Psicologia pela
              Universidade Católica de Santos, conciliando maternidade, trabalho e diversos
              cursos de formação no Brasil e no exterior.
            </p>
            <p>
              Como bolsista do CNPq, cursou o mestrado na Universidade Metodista de São Paulo na
              área de violência doméstica e familiar contra a mulher. Posteriormente, entre 482
              candidatos, foi classificada para uma das oito vagas do doutorado em Psicologia
              Clínica da <strong className="text-royal-800">Universidade de São Paulo (USP)</strong>.
            </p>
            <p>
              Ao longo de sua trajetória, atuou como docente e concluiu diversas especializações.
              Durante oito anos, desenvolveu pesquisas e ofereceu apoio psicológico a mulheres
              vítimas de violência doméstica nas Delegacias da Mulher de Santos, São Vicente e
              Rudge Ramos. Hoje soma <strong className="text-royal-800">mais de 26 anos</strong> de
              experiência clínica em consultório.
            </p>
            <p className="italic text-royal-800/90 border-l-4 border-gold-500 pl-5 py-2 bg-gold-50/50 rounded-r">
              “Meu objetivo é oferecer um espaço de escuta acolhedor, respeitoso e seguro,
              ajudando cada pessoa a compreender melhor suas dificuldades e a encontrar caminhos
              para uma vida mais equilibrada e saudável.”
            </p>
          </div>

          <div className="mt-12 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-royal-800 to-royal-900 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-gold-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-gold-500 text-royal-900 flex items-center justify-center">
                  <GraduationCap size={20} />
                </div>
                <span className="text-[11px] tracking-[0.25em] uppercase text-gold-500 font-semibold">
                  Excelência acadêmica
                </span>
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-white">
                Formação acadêmica
              </h3>
              <p className="mt-2 text-sm text-white/70 max-w-xl">
                Mais de 26 anos de estudo contínuo nas melhores instituições do Brasil — USP, FMUSP, UMESP e CEPSIC.
              </p>

              <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                {EDUCATION.map((e, i) => (
                  <li
                    key={i}
                    data-testid={`education-item-${i}`}
                    className="p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 hover:border-gold-500/40 transition-all backdrop-blur-sm"
                  >
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 size={16} className="text-gold-500 mt-1 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-white">{e.title}</p>
                        <p className="text-xs text-white/60 mt-0.5">
                          {e.institution}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
